import { useLoaderData, useLocation, useRevalidator } from "react-router";
import React, { useEffect, useRef, useState } from "react";
import useEvidencing from "./hooks/evidencing";
import SidebarContent from "~/components/annotate/annotate-sidebar";
import { BottomBarContent } from "~/components/annotate/annotate-bottombar";
import UploadManager from "~/components/upload-manager/upload-manager";
import { Trans } from "@lingui/react/macro";
import { useArticle } from "~/components/annotate/hooks/use-article";
import {
  ProgressContext,
  useProgressReporter,
} from "./hooks/use-progress-repoter";
import { AnnotateViewPdf } from "~/components/annotate/annotate-view-pdf";
import { AnnotateViewMultimedia } from "~/components/annotate/annotate-view-multimedia";
import { AnnotateViewHtml } from "~/components/annotate/annotate-view-html";
import ResourceNotesPanel from "~/components/annotate/panel-components/resource-note-panel";
import HighlightNotesPanel from "~/components/annotate/panel-components/highlight-note-panel";
import TagsPanel from "~/components/annotate/panel-components/tags-panel";
import PointsPanel from "./panels/points-panel";
import { Dialog, Sheet, SheetContent } from "@mindstonehq/ui";
import useAnnotationManager from "./hooks/annotation-manager";
import AnnotateMobileHeader from "~/components/annotate/annotate-mobile-header";
import {
  AnnotateArticleQuery,
  EvidencingFragment,
  ResourceCommentFragment,
  FullResourceFragment,
} from "~/graphql/codegen/graphql";
import { motion } from "framer-motion";
import HighlightCommentsPanel from "./panel-components/highlight-comments-panel";
import ResourceCommentsPanel from "./panel-components/resource-comments-panel";
import { FocusModeContext } from "~/components/annotate/hooks/focus-mode";
import {
  IHighlightCommon,
  IHtmlHighlight,
  IHtmlHighlightInput,
  IPdfHighlight,
  IPdfHighlightInput,
} from "~/typings/annotate/annotation";
import { HighlightContext } from "./hooks/use-selected-highlight";

export enum AnnotatePanels {
  ResourceNotes = "resource-notes",
  HighlightNote = "highlight-note",
  ResourceComments = "resource-comments",
  HighlightComments = "highlight-comments",
  Tags = "tags",
  Details = "details",
  Points = "points",
  // Queue = "queue",
}

export interface AnnotatePanelProps {
  article: FullResourceFragment;
  close: () => void;
  annotations: {
    comments: ResourceCommentFragment[];
    highlights: (IHtmlHighlight | IPdfHighlight)[];
  };
  selectedHighlight:
    | IHtmlHighlightInput
    | IPdfHighlightInput
    | IHighlightCommon
    | null
    | undefined;
  setSelectedHighlight: (
    highlight:
      | IHtmlHighlightInput
      | IPdfHighlightInput
      | IHighlightCommon
      | null
      | undefined,
  ) => void;
  refresh: () => void;
  goBackUrl?: string;
  startingText?: string;
  setPanel?: (panel: AnnotatePanels) => void;
}

enum PanelType {
  Modal = "modal",
  Sheet = "sheet",
}
const panelComponents = {
  [AnnotatePanels.ResourceNotes]: {
    component: ResourceNotesPanel,
    type: PanelType.Modal,
  },
  [AnnotatePanels.HighlightNote]: {
    component: HighlightNotesPanel,
    type: PanelType.Modal,
  },
  [AnnotatePanels.ResourceComments]: {
    component: ResourceCommentsPanel,
    type: PanelType.Sheet,
  },
  [AnnotatePanels.HighlightComments]: {
    component: HighlightCommentsPanel,
    type: PanelType.Sheet,
  },
  [AnnotatePanels.Tags]: {
    component: TagsPanel,
    type: PanelType.Sheet,
  },
  [AnnotatePanels.Details]: {
    component: SidebarAsSheetWrapper,
    type: PanelType.Sheet,
  },
  [AnnotatePanels.Points]: {
    component: PointsPanel,
    type: PanelType.Sheet,
  },
  // [AnnotatePanels.Queue]: QueuePanel,
};
export default function Annotate({
  loadedArticle,
}: {
  loadedArticle: AnnotateArticleQuery["readResource"];
}) {
  const [focused, setFocused] = useState(false);
  const article = useArticle(loadedArticle);
  const evidencing = useEvidencing(article?.id || "", article?.evidencing);
  const [progressContext, currentProgress] = useProgressReporter(article?.id);
  const panelRef = useRef<HTMLDivElement>(null);
  const remixLocation = useLocation();
  const [panel, setPanel] = useState<AnnotatePanels | null>(null);
  //TODO: Cleanup this when working on highlight, it's messy.
  const [selectedHighlight, setSelectedHighlight] = useState<
    | IHighlightCommon
    | IPdfHighlightInput
    | IHtmlHighlight
    | IHtmlHighlightInput
    | null
    | undefined
  >(null);
  const [startingText, setStartingText] = useState("");

  const { revalidate } = useRevalidator();
  // useEffect(() => {
  //   if (!article) {
  //     return;
  //   }
  //   commandPalette.setContext({
  //     type: "resource",
  //     data: article,
  //     afterAction: () => {
  //       revalidate();
  //     },
  //   });
  //   return () => commandPalette.setContext({ type: "none" });
  // }, [article]);

  useEffect(() => {
    const idInUrl = remixLocation?.hash.replace("#", "");
    if (!idInUrl || !article?.annotations?.highlights?.length) {
      const resourceLastReadIndex =
        article?.userContext?.userProgress.lastIndexID || 0;
      if (
        resourceLastReadIndex <= 1 ||
        article?.userContext?.userProgress.lastTimestamp
      ) {
        return;
      }
      const timeout = setTimeout(() => {
        const element = document.querySelector(
          // eslint-disable-next-line lingui/no-unlocalized-strings -- CSS selector
          `[data-index="${resourceLastReadIndex}"]`,
        );
        if (!element) {
          return;
        }
        element.scrollIntoView({ behavior: "instant", block: "start" });
      }, 100);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [remixLocation, !article?.annotations.highlights]);

  //TODO: Refactor this once we work on the new comment system
  const Panel = panel ? panelComponents[panel].component : null;
  const panelType = panel ? panelComponents[panel].type : null;
  const [comments, highlights] = useAnnotationManager(
    article?.id || "",
    article?.annotations,
  );

  if (!article) {
    return <></>;
  }
  return (
    <div className="h-screen w-screen flex flex-row" ref={panelRef}>
      <FocusModeContext.Provider
        value={{
          toggle: setFocused,
          current: focused,
        }}
      >
        <HighlightContext.Provider
          value={{
            selectedHighlight,
            openComments: (h, text) => {
              setSelectedHighlight(h);
              setStartingText(text || "");
              setPanel(AnnotatePanels.HighlightComments);
            },
            openInvestigator: (h) => {
              setSelectedHighlight(h);
              // setPanel(AnnotatePanels.Investigator);
            },
            openNote: (h, text) => {
              setSelectedHighlight(h);
              setStartingText(text || "");
              setPanel(AnnotatePanels.HighlightNote);
            },
          }}
        >
          <div
            className={
              "hidden md:flex md:w-[240px] bg-muted p-2 md:p-6  shrink-0"
            }
          >
            <SidebarContent
              article={article}
              evidencing={evidencing}
              setPanel={setPanel}
            />
          </div>
          <ProgressContext.Provider value={progressContext}>
            <div className="flex flex-row overflow-hidden  w-full grow">
              <div className="flex flex-col h-full w-full">
                <AnnotateMobileHeader
                  toggle={() =>
                    setPanel(
                      panel === AnnotatePanels.Details
                        ? null
                        : AnnotatePanels.Details,
                    )
                  }
                  open={panel === AnnotatePanels.Details}
                />
                <motion.div
                  style={{ scaleX: currentProgress || 0 }}
                  className="sticky w-full top-0 left-0 right-0 h-1 bg-primary origin-[0%] z-50"
                ></motion.div>

                {article.type === "html" && (
                  <AnnotateViewHtml article={article} />
                )}
                {article.type === "pdf" && (
                  <AnnotateViewPdf article={article} />
                )}
                {(article.type === "video" || article.type === "audio") && (
                  <AnnotateViewMultimedia article={article} />
                )}
                {!["pdf", "html", "audio", "video"].includes(article.type) && (
                  <p><Trans>Unsupported article type</Trans></p>
                )}
                <div
                  className={
                    "bg-muted-hover p-4 text-foreground shrink-0 flex flex-row justify-between items-center"
                  }
                >
                  <UploadManager>
                    <BottomBarContent
                      article={article}
                      evidencing={evidencing}
                      setPanel={setPanel}
                    />
                  </UploadManager>
                </div>
              </div>
            </div>
          </ProgressContext.Provider>
        </HighlightContext.Provider>
      </FocusModeContext.Provider>
      <Sheet
        open={!!Panel && panelType === PanelType.Sheet}
        onOpenChange={(open) => {
          if (!open) setPanel(null);
        }}
      >
        <SheetContent className={"sm:max-w-full w-full md:max-w-md"}>
          {Panel && panelType === PanelType.Sheet && (
            <Panel
              article={article}
              annotations={{
                highlights: highlights as IHtmlHighlight[],
                comments,
              }}
              evidencing={evidencing}
              refresh={revalidate}
              selectedHighlight={selectedHighlight}
              setSelectedHighlight={setSelectedHighlight}
              close={() => setPanel(null)}
              startingText={startingText}
              setPanel={setPanel}
            />
          )}
        </SheetContent>
      </Sheet>
      <Dialog
        open={!!Panel && panelType === PanelType.Modal}
        onOpenChange={(o) => {
          if (!o) setPanel(null);
        }}
      >
        {Panel && panelType === PanelType.Modal && (
          <Panel
            article={article}
            annotations={{
              highlights: highlights as IHtmlHighlight[],
              comments,
            }}
            evidencing={evidencing}
            refresh={revalidate}
            selectedHighlight={selectedHighlight}
            setSelectedHighlight={setSelectedHighlight}
            close={() => setPanel(null)}
            startingText={startingText}
            setPanel={setPanel}
          />
        )}
      </Dialog>
    </div>
  );
}

function SidebarAsSheetWrapper({
  article,
  evidencing,
  setPanel,
}: {
  article: FullResourceFragment;
  evidencing: EvidencingFragment;
  setPanel: (panel: AnnotatePanels) => void;
}) {
  return (
    <div className="md:flex h-full md:flex-col space-y-6 shrink-0 p-3">
      <SidebarContent
        article={article}
        evidencing={evidencing}
        setPanel={setPanel}
      />
    </div>
  );
}
